import Axios from "axios";
import { API_ROUTE, Routes } from "./Routes";

// A promotion code only proves eligibility for an offer. It is held for this browser session so it
// survives the sign-up -> verify -> sign-in journey, then claimed once the user is authenticated.
const PENDING_KEY = "fs_pending_promotion";

export type PromotionValidation = {
  valid: boolean;
  message: string;
  promotionName?: string | null;
  productName?: string | null;
  price?: number | null;
  expiresAt?: string | null;
  freeMonths?: number | null;
  requiresPayment?: boolean;
};

export const normalizePromotionCode = (value: string): string => value.trim().toUpperCase().replace(/\s+/g, "");

export const getPendingPromotionCode = (): string | null => {
  try {
    return window.sessionStorage.getItem(PENDING_KEY);
  } catch {
    return null;
  }
};

export const setPendingPromotionCode = (code: string): void => {
  try {
    const normalized = normalizePromotionCode(code);
    if (normalized) window.sessionStorage.setItem(PENDING_KEY, normalized);
  } catch {
    // Storage may be unavailable; the user can enter the code again after signing in.
  }
};

export const clearPendingPromotionCode = (): void => {
  try {
    window.sessionStorage.removeItem(PENDING_KEY);
  } catch {
    // ignore
  }
};

export const capturePromotionFromSearch = (search: string): string | null => {
  const code = normalizePromotionCode(new URLSearchParams(search).get("promo") ?? "");
  if (code) setPendingPromotionCode(code);
  return code || null;
};

export const validatePromotion = async (email: string, code: string): Promise<PromotionValidation> => {
  try {
    const response = await Axios.post<PromotionValidation>(`${API_ROUTE}${Routes.PROMOTIONS_VALIDATE}`, {
      email: email.trim(),
      code: normalizePromotionCode(code),
    });
    return response.data;
  } catch (error) {
    const status = Axios.isAxiosError(error) ? error.response?.status : undefined;
    return {
      valid: false,
      message: status === 429
        ? "Too many attempts. Please wait a minute and try again."
        : "The promotion code is invalid or cannot be used with this email address.",
    };
  }
};
